import React, {useEffect, useRef, useState} from 'react';
import {InputAdornment, TextField} from '@mui/material';
import Box from '@mui/material/Box';
import {useTranslation} from "react-i18next";
import {useTheme} from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import {ClearIcon, DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import CustomTooltip from "../CustomTooltip";

// I campi numerici sono renderizzati come input di testo: <input type="number"> delega il
// parsing al locale del browser, quindi con locale it-IT "115.05" e' un valore non valido e
// il browser restituisce "" (il campo si azzera). Qui accettiamo sia "." che "," e
// normalizziamo sempre sul punto prima di propagare il valore al chiamante.
const DECIMAL_DRAFT_PATTERN = /^-?\d*([.,]\d*)?$/;

const normalizeDecimal = (draft: string) => draft.replace(',', '.');

const toNumeric = (draft: string) => {
    const normalized = normalizeDecimal(draft).trim();
    return normalized === '' ? NaN : Number(normalized);
};

const toDraft = (value?: string | number | null) => {
    if (value === undefined || value === null || value === '') return '';
    if (typeof value === 'number' && Number.isNaN(value)) return '';
    return String(value);
};

interface MuiTextFieldProps {
    label: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement> | any) => void;
    inputRef?: React.RefObject<HTMLInputElement>;
    borderColor?: string;
    onFocusBorderColor?: string;
    type?: string;
    value?: string | number | null;
    required?: boolean;
    step?: string;
    rows?: number;
    disabled?: boolean;
    toolTipInfo?: string;
    children?: React.ReactNode;
}

const MuiInputTextField: React.FC<MuiTextFieldProps> = ({
                                                            label,
                                                            onChange,
                                                            inputRef,
                                                            borderColor,
                                                            onFocusBorderColor,
                                                            type = 'text',
                                                            value,
                                                            required,
                                                            step,
                                                            rows,
                                                            disabled = false,
                                                            toolTipInfo,
                                                            children,
                                                        }) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);

    const _borderColor = borderColor ? borderColor : theme.palette.primary.light;
    const _onFocusBorderColor = onFocusBorderColor ? onFocusBorderColor : theme.palette.primary.dark;

    useEffect(() => {
        if (inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    }, [inputRef]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (type === 'number' && e.key === 'e') {
            e.preventDefault();
        }
    };

    const isNumber = type === 'number';
    const [numberDraft, setNumberDraft] = useState(() => toDraft(value));
    const numberDraftRef = useRef(numberDraft);
    numberDraftRef.current = numberDraft;

    useEffect(() => {
        if (!isNumber) return;
        const incoming = toDraft(value);
        const incomingNumeric = toNumeric(incoming);
        const draftNumeric = toNumeric(numberDraftRef.current);
        // Stesso numero (o entrambi vuoti/incompleti): mantengo quello che sta scrivendo
        // l'utente, cosi' "115," o "115." non vengono riscritti a meta' digitazione.
        if (incomingNumeric === draftNumeric) return;
        if (Number.isNaN(incomingNumeric) && Number.isNaN(draftNumeric)) return;
        setNumberDraft(incoming);
    }, [isNumber, value]);

    const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const draft = event.target.value;
        if (!DECIMAL_DRAFT_PATTERN.test(draft)) return;
        setNumberDraft(draft);
        onChange({
            ...event,
            target: {...event.target, value: normalizeDecimal(draft)},
        });
    };


    return (
        <CustomTooltip title={toolTipInfo}>
            <Box sx={{position: 'relative', width: '100%'}}>
                {children && (
                    <Box sx={{position: 'absolute', top: -10, right: 0, display: 'flex', alignItems: 'center', gap: 2}}
                    >
                        {children}
                    </Box>
                )}
                <Box sx={{marginTop: children ? '24px' : '24px'}}>
                    {type === 'date' ? (
                        <DatePicker
                            name={t(label)}
                            label={t(label)}
                            views={['year', 'month', 'day']}
                            sx={{
                                bgcolor: 'white',

                                '& label.Mui-focused': {
                                    color: _borderColor,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: 1,
                                    borderColor: _borderColor,
                                },
                                '& .MuiInput-underline:after': {
                                    borderBottomColor: _onFocusBorderColor,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: _borderColor,
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: _borderColor,
                                },
                                '& .MuiFilledInput-underline:after': {
                                    borderBottomColor: _onFocusBorderColor,
                                },
                                '& .MuiFilledInput-underline:before': {
                                    borderBottomColor: _borderColor,
                                },
                                '&:hover .MuiFilledInput-underline:before': {
                                    borderBottomColor: _borderColor,
                                },
                                '& .MuiFilledInput-root.Mui-focused .MuiFilledInput-underline:after': {
                                    borderBottomColor: _borderColor,
                                },

                            }}
                            slotProps={{
                                textField: {
                                    size: 'medium',
                                    fullWidth: true,
                                    InputProps: {
                                        startAdornment: (
                                            <>
                                                {value && !required && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => {
                                                                onChange('')
                                                            }}
                                                            disabled={disabled || !value}
                                                        >
                                                            <ClearIcon/>
                                                        </IconButton>
                                                    </InputAdornment>
                                                )}
                                            </>
                                        ),
                                    },
                                },
                            }}
                            // sx={{width: "18rem"}}
                            format="DD/MM/YY"
                            disabled={disabled}
                            value={value ? dayjs(value) : null}
                            onChange={(value) => {
                                if (value && value.format() === "Invalid Date") return
                                const dateValue = value ? value.format("YYYY-MM-DD") : undefined
                                onChange(dateValue || "");
                            }}
                        />
                    ) : (
                        <TextField
                            type={isNumber ? 'text' : type}
                            slotProps={isNumber ? {htmlInput: {inputMode: 'decimal'}} : undefined}
                            label={t(label)}
                            onKeyDown={handleKeyDown}
                            onChange={isNumber ? handleNumberChange : onChange}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            inputRef={inputRef}
                            fullWidth
                            value={isNumber ? numberDraft : (value !== undefined && value !== null ? value : '')}
                            required={required}
                            multiline={!!(rows && rows > 1)}
                            rows={rows}
                            margin="dense"
                            autoComplete="off"
                            size="small"
                            variant="filled"
                            disabled={disabled}
                            sx={{
                                bgcolor: 'white',
                                '& label.Mui-focused': {
                                    color: _borderColor,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderWidth: 1,
                                    borderColor: _borderColor,
                                },
                                '& .MuiInput-underline:after': {
                                    borderBottomColor: _onFocusBorderColor,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: _borderColor,
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: _borderColor,
                                },
                                '& .MuiFilledInput-underline:after': {
                                    borderBottomColor: _onFocusBorderColor,
                                },
                                '& .MuiFilledInput-underline:before': {
                                    borderBottomColor: _borderColor,
                                },
                                '&:hover .MuiFilledInput-underline:before': {
                                    borderBottomColor: _borderColor,
                                },
                                '& .MuiFilledInput-root.Mui-focused .MuiFilledInput-underline:after': {
                                    borderBottomColor: _borderColor,
                                },
                            }}
                        />
                    )}
                </Box>

            </Box>

        </CustomTooltip>

    );
};

export default MuiInputTextField;
